import {
  useCallback, useMemo 
} from "react"

export const usePagination = (props = {}) => {
  const {
    currentPage,
    setPage,
    afterClick = () => { },
    totalPages,
    tileBuffer = 2,
    boundaryCount = 1,
    siblingCount = 1,
    showFirstButton = false,
    showLastButton = false,
    hideNextButton = false,
    hidePrevButton = false,
  } = props

  const getLeadTile = useCallback(
    (currentPage, totalPages) => {
      const selectedTile = currentPage
      const leftBound = 1 + tileBuffer
      const rightBound = totalPages - tileBuffer

      let leadTile = selectedTile - tileBuffer

      if (selectedTile < leftBound) {
        leadTile = 1
      } else if (selectedTile > rightBound) {
        leadTile = Math.max(totalPages - tileBuffer * 2, 1)
      }

      return leadTile
    },
    [tileBuffer]
  )

  const handleLeftSkip = useCallback(() => {
    const leadTile = getLeadTile(currentPage, totalPages)

    const leftSkip = Math.max(1, leadTile - (tileBuffer + 1))

    setPage(leftSkip)

    afterClick && afterClick()
  }, [currentPage, getLeadTile, setPage, afterClick, totalPages, tileBuffer])

  const handleRightSkip = useCallback(() => {
    const leadTile = getLeadTile(currentPage, totalPages)
    const rightSkip = Math.min(
      totalPages,
      leadTile + tileBuffer * 2 + (tileBuffer + 1)
    )

    setPage(rightSkip)
    afterClick && afterClick()

  }, [currentPage, getLeadTile, setPage, afterClick, totalPages, tileBuffer])

  const handleNavBack = useCallback(() => {
    if (currentPage > 1) {
      setPage(currentPage - 1)

      afterClick && afterClick()

    }
  }, [currentPage, setPage, afterClick])

  const handleNavForward = useCallback(() => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1)
      afterClick && afterClick()
    }
  }, [currentPage, setPage, afterClick, totalPages])

  const isActiveLeft = currentPage !== 1
  const isActiveRight = currentPage !== totalPages

  const range = (start, end) => {
    const length = end - start + 1

    return Array.from({
      length 
    }, (_, i) => start + i)
  }

  const tiles = useMemo(() => {
    const startPages = range(1, Math.min(boundaryCount, totalPages))
    const endPages = range(
      Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
      totalPages
    )

    const siblingsStart = Math.max(
      Math.min(
        // Natural start
        currentPage - siblingCount,
        // Lower boundary when page is high
        totalPages - boundaryCount - siblingCount * 2 - 1
      ),
      // Greater than startPages
      boundaryCount + 2
    )

    const siblingsEnd = Math.min(
      Math.max(
        // Natural end
        currentPage + siblingCount,
        // Upper boundary when page is low
        boundaryCount + siblingCount * 2 + 2
      ),
      // Less than endPages
      endPages.length > 0 ? endPages[0] - 2 : totalPages - 1
    )

    const tiles = [
      ...(showFirstButton ? ["first"] : []),
      ...(hidePrevButton ? [] : ["previous"]),
      ...startPages,

      // Start ellipsis
      // eslint-disable-next-line no-nested-ternary
      ...(siblingsStart > boundaryCount + 2
        ? ["start-ellipsis"]
        : boundaryCount + 1 < totalPages - boundaryCount
          ? [boundaryCount + 1]
          : []),

      // Sibling pages
      ...range(siblingsStart, siblingsEnd),

      // End ellipsis
      // eslint-disable-next-line no-nested-ternary
      ...(siblingsEnd < totalPages - boundaryCount - 1
        ? ["end-ellipsis"]
        : totalPages - boundaryCount > boundaryCount
          ? [totalPages - boundaryCount]
          : []),

      ...endPages,
      ...(hideNextButton ? [] : ["next"]),
      ...(showLastButton ? ["last"] : []),
    ]

    return tiles
  }, [totalPages, currentPage, hidePrevButton, hideNextButton])

  return {
    handleLeftSkip,
    handleRightSkip,
    handleNavBack,
    handleNavForward,
    isActiveLeft,
    isActiveRight,
    tiles,
  }
}
